<template>
  <div
    class="bb-table-wrapper mt-3 bb-popup-table-table-wrapper"
    id="board"
  >
    <div class="bb-popup-table-controls mb-3">
      <div
        v-if="searchEnabled"
        class="bb-viewer-search-shell"
      >
        <div class="form-group bb-viewer-search-wrap">
          <div class="form-floating">
            <input
              type="text"
              id="bb-party-runes-search-input"
              :value="searchTerm"
              @input="handleSearchInput"
              class="form-control bb-viewer-search-input"
              placeholder="search..."
            />
            <label
              class="bb-float-label"
              for="bb-party-runes-search-input"
              >Rune</label
            >
          </div>
        </div>
      </div>
    </div>

    <vue-good-table
      :columns="tableColumns"
      :rows="tableRows"
      :pagination-options="paginationOptions"
      :row-style-class="rowStyleClassFn"
      @cell-click="handleCellClick"
      styleClass="vgt-table bordered"
      theme="black-rhino"
    >
      <template #table-column="props">
        <span
          v-if="
            props.column.field === 'rune_name' ||
            props.column.field === 'item_name' ||
            props.column.field === 'held_by_name'
          "
        >
          {{ props.column.label }}
        </span>
        <div
          v-else
          class="bb-viewer-column-heading"
        >
          <span class="bb-viewer-column-name">
            {{ props.column.label }}
          </span>
          <span class="bb-viewer-column-subtitle">
            {{ props.column.playerLabel }}
          </span>
        </div>
      </template>

      <template #table-row="props">
        <span
          v-if="
            props.column.field === 'rune_name' ||
            props.column.field === 'item_name' ||
            props.column.field === 'held_by_name'
          "
          class="bb-party-runes-meta-cell"
        >
          {{
            props.row.__placeholder
              ? ''
              : props.formattedRow[props.column.field]
          }}
        </span>
        <span v-else>
          <img
            v-if="
              interactiveFailedAttempts &&
              isFailedCellValue(
                props.row,
                props.formattedRow,
                props.column.field,
              )
            "
            :src="cancelImg"
            class="bb-party-runes-failed-icon"
            height="56"
            width="56"
            alt="failed attempt"
          />
          <span v-else>
            {{
              formatValue(
                getCellValue(props.row, props.formattedRow, props.column.field),
              )
            }}
          </span>
        </span>
      </template>

      <template #emptystate></template>
    </vue-good-table>

    <div
      v-if="showStandaloneEmptyMessage"
      class="mt-2 mb-3"
    >
      <div class="form-group">
        <div class="row mx-0">
          <div class="col text-end me-3">
            <div
              class="bb-error-wrap"
              style="white-space: pre-line"
            >
              <div class="row mb-3">
                {{ emptyMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'

import { VueGoodTable } from 'vue-good-table-next'
import cancelImg from '../assets/images/bbCancel100.png'

export default {
  name: 'PartyRunesTable',
  components: {
    VueGoodTable,
  },
  emits: ['update:searchTerm', 'toggle-failed-attempt'],
  props: {
    partyCombatants: {
      type: Array,
      default: () => [],
    },
    npcCombatants: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    emptyMessage: {
      type: String,
      default: 'No runes to display.',
    },
    searchTerm: {
      type: String,
      default: '',
    },
    searchEnabled: {
      type: Boolean,
      default: true,
    },
    interactiveFailedAttempts: {
      type: Boolean,
      default: false,
    },
    showItemColumn: {
      type: Boolean,
      default: false,
    },
    showHeldColumn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cancelImg,
    }
  },
  computed: {
    allCombatants() {
      return [...this.partyCombatants, ...this.npcCombatants]
    },
    paginationOptions() {
      return {
        enabled: false,
        mode: 'records',
        perPage: 8,
        position: 'bottom',
        dropdownAllowAll: true,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Runes per page',
        ofLabel: 'of',
        allLabel: 'All',
      }
    },
    tableColumns() {
      return [
        {
          label: 'Rune',
          field: 'rune_name',
          width: '9rem',
          thClass: 'bb-party-runes-column-rune',
          tdClass: 'bb-party-runes-column-rune',
        },
        ...(this.showItemColumn
          ? [
              {
                label: 'Item',
                field: 'item_name',
                width: '12rem',
                thClass: 'bb-party-runes-column-item',
                tdClass: 'bb-party-runes-column-item',
              },
            ]
          : []),
        ...(this.showHeldColumn
          ? [
              {
                label: 'Held',
                field: 'held_by_name',
                width: '9rem',
                thClass: 'bb-party-runes-column-held',
                tdClass: 'bb-party-runes-column-held',
              },
            ]
          : []),
        ...this.partyCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'No player',
          width: '6rem',
          thClass: 'bb-party-runes-column-combatant',
          tdClass: 'bb-party-runes-column-combatant',
        })),
        ...this.npcCombatants.map((combatant) => ({
          label: combatant.character_name,
          field: this.getCombatantField(combatant),
          playerLabel: combatant.player_name || 'NPC',
          width: '6rem',
          thClass: 'bb-party-runes-column-combatant',
          tdClass: 'bb-party-runes-column-combatant',
        })),
      ]
    },
    shouldShowPlaceholderRow() {
      return (
        this.allCombatants.length > 0 &&
        this.rows.length === 0 &&
        !this.searchTerm.trim()
      )
    },
    showStandaloneEmptyMessage() {
      return (
        this.rows.length === 0 &&
        !this.shouldShowPlaceholderRow &&
        !!this.emptyMessage
      )
    },
    tableRows() {
      if (this.shouldShowPlaceholderRow) {
        const placeholderRow = {
          rune_name: ' ',
          __placeholder: true,
        }

        if (this.showItemColumn) {
          placeholderRow.item_name = ''
        }

        if (this.showHeldColumn) {
          placeholderRow.held_by_name = ''
        }

        this.allCombatants.forEach((combatant) => {
          placeholderRow[this.getCombatantField(combatant)] = ''
        })

        return [placeholderRow]
      }

      return this.rows.map((row) => {
        const mappedRow = {
          __characterRuneId: row.character_rune_id || '',
          __baseValues: row.base_values || {},
          __failedCharacterIds: row.failed_character_ids || [],
          rune_name: row.rune_name,
          item_name: row.item_name || '',
          held_by_name: row.held_by_name || '',
        }

        this.allCombatants.forEach((combatant) => {
          mappedRow[this.getCombatantField(combatant)] =
            row.values?.[combatant.character_id] ?? ''
        })

        return mappedRow
      })
    },
  },
  methods: {
    handleCellClick(params) {
      if (
        !this.interactiveFailedAttempts ||
        params?.column?.field === 'rune_name' ||
        params?.column?.field === 'item_name' ||
        params?.column?.field === 'held_by_name' ||
        params?.row?.__placeholder ||
        !params?.row?.__characterRuneId
      ) {
        return
      }

      const characterId = String(params.column.field || '').replace(
        /^character_/,
        '',
      )

      if (!characterId) {
        return
      }

      this.$emit('toggle-failed-attempt', {
        characterRuneId: params.row.__characterRuneId,
        characterId,
      })
    },
    handleSearchInput(event) {
      this.$emit('update:searchTerm', event.target.value)
    },
    getCombatantField(combatant) {
      return `character_${combatant.character_id}`
    },
    getCellValue(row, formattedRow, field) {
      const formattedValue = formattedRow?.[field]

      if (formattedValue !== undefined && formattedValue !== null) {
        return formattedValue
      }

      return row?.[field]
    },
    getCharacterIdFromField(field) {
      return String(field || '').replace(/^character_/, '')
    },
    isFailedCellValue(row, formattedRow, field) {
      const cellValue = String(
        this.getCellValue(row, formattedRow, field) || '',
      ).trim()

      if (cellValue === 'Failed') {
        return true
      }

      const characterId = this.getCharacterIdFromField(field)
      if (!characterId) {
        return false
      }

      const failedCharacterIds = Array.isArray(row?.__failedCharacterIds)
        ? row.__failedCharacterIds
        : Array.isArray(formattedRow?.__failedCharacterIds)
          ? formattedRow.__failedCharacterIds
          : []

      return failedCharacterIds.map(String).includes(characterId)
    },
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '' : value
    },
    rowStyleClassFn(row) {
      if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      }

      return 'rowParty'
    },
  },
}
</script>

<style scoped>
.bb-party-runes-failed-icon {
  cursor: pointer;
  width: 56px;
  height: 56px;
  max-width: none;
}
</style>

<style scoped>
.bb-popup-table-table-wrapper {
  height: 64vh;
  max-height: 64vh;
  overflow: auto;
  padding: 0.2rem 0.2rem 1rem;
}

.bb-popup-table-controls {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.bb-viewer-search-input {
  color: white !important;
  -webkit-text-fill-color: white !important;
  caret-color: white !important;
  opacity: 1 !important;
}

.bb-viewer-search-shell {
  width: min(14rem, 100%);
  max-width: 14rem;
}

.bb-viewer-search-wrap {
  width: 100%;
  max-width: 14rem;
}

:deep(.bb-viewer-search-wrap .form-floating) {
  width: 100%;
}

.bb-party-runes-meta-cell {
  display: block;
  width: 100%;
  text-align: left;
}

.bb-viewer-column-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  min-width: 0;
}

.bb-viewer-column-name {
  color: white;
  font-weight: inherit;
  font-size: inherit;
  line-height: 1.2;
}

.bb-viewer-column-subtitle {
  color: #737373;
  font-weight: 400;
  line-height: 1.2;
}

:deep(.vgt-table) {
  min-width: 50rem;
  table-layout: fixed;
}

:deep(.vgt-table thead th) {
  vertical-align: bottom;
  white-space: normal;
  padding-top: 10px;
  padding-bottom: 8px;
}

:deep(.vgt-table thead th.bb-party-runes-column-rune),
:deep(.vgt-table tbody td.bb-party-runes-column-rune) {
  width: 9rem;
  min-width: 9rem;
  max-width: 9rem;
}

:deep(.vgt-table thead th.bb-party-runes-column-item),
:deep(.vgt-table tbody td.bb-party-runes-column-item) {
  width: 12rem;
  min-width: 12rem;
  max-width: 12rem;
}

:deep(.vgt-table thead th.bb-party-runes-column-held),
:deep(.vgt-table tbody td.bb-party-runes-column-held) {
  width: 9rem;
  min-width: 9rem;
  max-width: 9rem;
}

:deep(.vgt-table thead th.bb-party-runes-column-combatant),
:deep(.vgt-table tbody td.bb-party-runes-column-combatant) {
  width: 6rem;
}

:deep(.vgt-table tbody td),
:deep(.vgt-table tbody th) {
  vertical-align: middle;
}

:deep(.vgt-table tbody td.bb-party-runes-column-rune),
:deep(.vgt-table tbody td.bb-party-runes-column-item),
:deep(.vgt-table tbody td.bb-party-runes-column-held) {
  font-weight: 700;
  color: #c2ccd6;
  text-align: left;
}

:deep(.vgt-table tbody td.bb-party-runes-column-combatant) {
  text-align: center;
  font-weight: 600;
}

:deep(.vgt-wrap__footer) {
  border-top: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

@media (max-width: 768px) {
  .bb-popup-table-controls {
    align-items: stretch;
  }

  .bb-viewer-search-shell {
    width: 100%;
  }
}
</style>
